/**
 * mock.js - Central data file for Curis Path website
 * 
 * This file contains all the static data used throughout the website.
 * In a production environment, this data would come from a backend API/database.
 * 
 * Data Structure:
 * - hospitals: Partner hospital information
 * - doctors: Doctor profiles with qualifications and specializations
 * - specialties: Medical specialty categories with treatments
 * - testimonials: Patient testimonials/reviews
 * - whyChooseUs: Value proposition points
 * - procedureOptions: Enquiry form procedure dropdown options
 * - timeframeOptions: Enquiry form timeframe dropdown options
 * - contactInfo: Company contact information
 * 
 * To modify content:
 * 1. Find the relevant export (e.g., 'doctors' for doctor data)
 * 2. Update the object properties as needed
 * 3. For images, use direct URLs from Unsplash/Pexels or local paths in /public/doctors/
 * 
 * @author Curis Path Development Team
 */

// ============================================
// HOSPITALS DATA
// Partner hospitals with location and accreditation info
// ============================================
export const hospitals = [
  // Featured Partner Hospitals (shown on main page)
  {
    id: 1,
    name: "Max Super Speciality Hospital, Saket",
    location: "New Delhi, India",
    specialties: ["Orthopedics", "Oncology", "Dental", "Cosmetic Surgery"],
    accreditation: "JCI & NABH Accredited",
    image: "https://images.unsplash.com/photo-1615770922480-0b9ae80afeba?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBob3NwaXRhbHxlbnwwfHx8fDE3NjkwNjk4MDJ8MA&ixlib=rb-4.1.0&q=85",
    description: "Leading multi-specialty hospital with state-of-the-art facilities",
    beds: "500+",
    patientsPerYear: "3.4 Million+",
    featured: true,
    group: "Max Healthcare"
  },
  {
    id: 2,
    name: "BLK-Max Super Speciality Hospital",
    location: "New Delhi, India",
    specialties: ["Orthopedics", "Oncology", "Dental"],
    accreditation: "NABH Accredited",
    image: "https://images.pexels.com/photos/7250797/pexels-photo-7250797.jpeg",
    description: "Premier multi-specialty hospital with advanced critical care",
    beds: "650+",
    patientsPerYear: "2 Million+",
    featured: true,
    group: "Max Healthcare"
  },
  {
    id: 3,
    name: "Nanavati Max Super Speciality Hospital",
    location: "Mumbai, India",
    specialties: ["Orthopedics", "Oncology", "Cosmetic Surgery"],
    accreditation: "JCI & NABH Accredited",
    image: "https://images.unsplash.com/photo-1719934398679-d764c1410770?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBob3NwaXRhbHxlbnwwfHx8fDE3NjkwNjk4MDJ8MA&ixlib=rb-4.1.0&q=85",
    description: "Premier healthcare destination in Mumbai with international standards",
    beds: "350+",
    patientsPerYear: "2 Million+",
    featured: true,
    group: "Max Healthcare"
  },
  {
    id: 4,
    name: "Medanta - The Medicity",
    location: "Gurugram, India",
    specialties: ["Orthopedics", "Oncology", "Dental", "Cosmetic Surgery"],
    accreditation: "JCI & NABH Accredited",
    image: "https://images.pexels.com/photos/7250788/pexels-photo-7250788.jpeg",
    description: "World-class medical care with cutting-edge technology",
    beds: "1,400+",
    patientsPerYear: "4 Million+",
    featured: true,
    group: "Medanta"
  },
  {
    id: 5,
    name: "Artemis Hospital",
    location: "Gurugram, India",
    specialties: ["Orthopedics", "Oncology", "Cosmetic Surgery"],
    accreditation: "JCI & NABH Accredited",
    image: "https://images.pexels.com/photos/7250797/pexels-photo-7250797.jpeg",
    description: "Advanced tertiary care with robotic surgery capabilities",
    beds: "750+",
    patientsPerYear: "1.8 Million+",
    featured: true,
    group: "Artemis"
  },
  {
    id: 6,
    name: "Indraprastha Apollo Hospitals",
    location: "New Delhi, India",
    specialties: ["Orthopedics", "Oncology", "Dental", "Cosmetic Surgery"],
    accreditation: "JCI & NABH Accredited",
    image: "https://images.unsplash.com/photo-1615770922480-0b9ae80afeba?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBob3NwaXRhbHxlbnwwfHx8fDE3NjkwNjk4MDJ8MA&ixlib=rb-4.1.0&q=85",
    description: "Comprehensive healthcare with personalized patient care",
    beds: "700+",
    patientsPerYear: "3 Million+",
    featured: true,
    group: "Apollo"
  },
  {
    id: 7,
    name: "Apollo Hospitals",
    location: "Chennai, India",
    specialties: ["Orthopedics", "Oncology", "Dental"],
    accreditation: "JCI & NABH Accredited",
    image: "https://images.pexels.com/photos/7250788/pexels-photo-7250788.jpeg",
    description: "Flagship hospital with excellence in cardiac and cancer care",
    beds: "600+",
    patientsPerYear: "2.5 Million+",
    featured: true,
    group: "Apollo"
  },
  {
    id: 8,
    name: "Fortis Memorial Research Institute",
    location: "Gurugram, India",
    specialties: ["Orthopedics", "Oncology", "Dental", "Cosmetic Surgery"],
    accreditation: "JCI & NABH Accredited",
    image: "https://images.unsplash.com/photo-1719934398679-d764c1410770?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBob3NwaXRhbHxlbnwwfHx8fDE3NjkwNjk4MDJ8MA&ixlib=rb-4.1.0&q=85",
    description: "Advanced tertiary care with robotic surgery capabilities",
    beds: "1,000+",
    patientsPerYear: "2 Million+",
    featured: true,
    group: "Fortis"
  },
  {
    id: 9,
    name: "Fortis Escorts Heart Institute",
    location: "Okhla, New Delhi, India",
    specialties: ["Orthopedics", "Oncology"],
    accreditation: "JCI & NABH Accredited",
    image: "https://images.pexels.com/photos/7250797/pexels-photo-7250797.jpeg",
    description: "Renowned for cardiac care and multi-specialty excellence",
    beds: "310+",
    patientsPerYear: "1.5 Million+",
    featured: true,
    group: "Fortis"
  },
  
  // Additional Max Healthcare Hospitals
  {
    id: 10,
    name: "Max Super Speciality Hospital, Patparganj",
    location: "New Delhi, India",
    specialties: ["Orthopedics", "Oncology"],
    accreditation: "NABH Accredited",
    image: "https://images.unsplash.com/photo-1615770922480-0b9ae80afeba?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBob3NwaXRhbHxlbnwwfHx8fDE3NjkwNjk4MDJ8MA&ixlib=rb-4.1.0&q=85",
    description: "Multi-specialty hospital in East Delhi",
    beds: "250+",
    patientsPerYear: "1 Million+",
    featured: false,
    group: "Max Healthcare"
  },
  {
    id: 11,
    name: "Max Super Speciality Hospital, Shalimar Bagh",
    location: "New Delhi, India",
    specialties: ["Orthopedics", "Oncology"],
    accreditation: "NABH Accredited",
    image: "https://images.pexels.com/photos/7250788/pexels-photo-7250788.jpeg",
    description: "Comprehensive care in North Delhi",
    beds: "220+",
    patientsPerYear: "900,000+",
    featured: false,
    group: "Max Healthcare"
  },
  {
    id: 12,
    name: "Max Smart Super Speciality Hospital",
    location: "New Delhi, India",
    specialties: ["Orthopedics", "Oncology"],
    accreditation: "NABH Accredited",
    image: "https://images.unsplash.com/photo-1719934398679-d764c1410770?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBob3NwaXRhbHxlbnwwfHx8fDE3NjkwNjk4MDJ8MA&ixlib=rb-4.1.0&q=85",
    description: "Smart healthcare solutions with advanced facilities",
    beds: "150+",
    patientsPerYear: "700,000+",
    featured: false,
    group: "Max Healthcare"
  },
  {
    id: 13,
    name: "Max Super Speciality Hospital, Vaishali",
    location: "Ghaziabad, India",
    specialties: ["Orthopedics", "Oncology"],
    accreditation: "NABH Accredited",
    image: "https://images.pexels.com/photos/7250797/pexels-photo-7250797.jpeg",
    description: "Quality healthcare in NCR region",
    beds: "150+",
    patientsPerYear: "600,000+",
    featured: false,
    group: "Max Healthcare"
  },
  {
    id: 14,
    name: "Max Super Speciality Hospital, Mohali",
    location: "Mohali, Punjab, India",
    specialties: ["Orthopedics", "Oncology"],
    accreditation: "NABH Accredited",
    image: "https://images.unsplash.com/photo-1615770922480-0b9ae80afeba?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBob3NwaXRhbHxlbnwwfHx8fDE3NjkwNjk4MDJ8MA&ixlib=rb-4.1.0&q=85",
    description: "Leading hospital in Punjab region",
    beds: "200+",
    patientsPerYear: "800,000+",
    featured: false,
    group: "Max Healthcare"
  },
  {
    id: 15,
    name: "Max Super Speciality Hospital, Bathinda",
    location: "Bathinda, Punjab, India",
    specialties: ["Orthopedics", "Oncology"],
    accreditation: "NABH Accredited",
    image: "https://images.pexels.com/photos/7250788/pexels-photo-7250788.jpeg",
    description: "Tertiary care in Punjab",
    beds: "180+",
    patientsPerYear: "500,000+",
    featured: false,
    group: "Max Healthcare"
  },
  
  // Additional Medanta Hospitals
  {
    id: 16,
    name: "Medanta Hospital",
    location: "Lucknow, India",
    specialties: ["Orthopedics", "Oncology"],
    accreditation: "NABH Accredited",
    image: "https://images.unsplash.com/photo-1719934398679-d764c1410770?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBob3NwaXRhbHxlbnwwfHx8fDE3NjkwNjk4MDJ8MA&ixlib=rb-4.1.0&q=85",
    description: "Super-specialty care in Uttar Pradesh",
    beds: "350+",
    patientsPerYear: "1.2 Million+",
    featured: false,
    group: "Medanta"
  },
  {
    id: 17,
    name: "Medanta Hospital",
    location: "Noida, India",
    specialties: ["Orthopedics", "Oncology"],
    accreditation: "NABH Accredited",
    image: "https://images.pexels.com/photos/7250797/pexels-photo-7250797.jpeg",
    description: "Multi-specialty hospital in NCR",
    beds: "300+",
    patientsPerYear: "1 Million+",
    featured: false,
    group: "Medanta"
  },
  {
    id: 18,
    name: "Medanta Hospital",
    location: "Indore, India",
    specialties: ["Orthopedics", "Oncology"],
    accreditation: "NABH Accredited",
    image: "https://images.unsplash.com/photo-1615770922480-0b9ae80afeba?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBob3NwaXRhbHxlbnwwfHx8fDE3NjkwNjk4MDJ8MA&ixlib=rb-4.1.0&q=85",
    description: "Advanced healthcare in Central India",
    beds: "250+",
    patientsPerYear: "900,000+",
    featured: false,
    group: "Medanta"
  },
  {
    id: 19,
    name: "Medanta Hospital",
    location: "Patna, India",
    specialties: ["Orthopedics", "Oncology"],
    accreditation: "NABH Accredited",
    image: "https://images.pexels.com/photos/7250788/pexels-photo-7250788.jpeg",
    description: "Quality tertiary care in Bihar",
    beds: "200+",
    patientsPerYear: "700,000+",
    featured: false,
    group: "Medanta"
  },
  
  // Additional Apollo Hospitals
  {
    id: 20,
    name: "Apollo Gleneagles Hospitals",
    location: "Kolkata, India",
    specialties: ["Orthopedics", "Oncology"],
    accreditation: "JCI & NABH Accredited",
    image: "https://images.unsplash.com/photo-1719934398679-d764c1410770?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBob3NwaXRhbHxlbnwwfHx8fDE3NjkwNjk4MDJ8MA&ixlib=rb-4.1.0&q=85",
    description: "Advanced tertiary care in Eastern India",
    beds: "500+",
    patientsPerYear: "1.5 Million+",
    featured: false,
    group: "Apollo"
  },
  {
    id: 21,
    name: "Apollo Hospitals",
    location: "Bangalore, India",
    specialties: ["Orthopedics", "Oncology"],
    accreditation: "JCI & NABH Accredited",
    image: "https://images.pexels.com/photos/7250797/pexels-photo-7250797.jpeg",
    description: "Multi-specialty excellence in South India",
    beds: "400+",
    patientsPerYear: "1.8 Million+",
    featured: false,
    group: "Apollo"
  },
  {
    id: 22,
    name: "Apollo Hospitals",
    location: "Hyderabad, India",
    specialties: ["Orthopedics", "Oncology"],
    accreditation: "JCI & NABH Accredited",
    image: "https://images.unsplash.com/photo-1615770922480-0b9ae80afeba?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBob3NwaXRhbHxlbnwwfHx8fDE3NjkwNjk4MDJ8MA&ixlib=rb-4.1.0&q=85",
    description: "Premier healthcare in Telangana",
    beds: "550+",
    patientsPerYear: "2 Million+",
    featured: false,
    group: "Apollo"
  },
  {
    id: 23,
    name: "Apollo Hospitals",
    location: "Ahmedabad, India",
    specialties: ["Orthopedics", "Oncology"],
    accreditation: "NABH Accredited",
    image: "https://images.pexels.com/photos/7250788/pexels-photo-7250788.jpeg",
    description: "Quality care in Gujarat",
    beds: "300+",
    patientsPerYear: "1.2 Million+",
    featured: false,
    group: "Apollo"
  },
  {
    id: 24,
    name: "Apollo Hospitals",
    location: "Navi Mumbai, India",
    specialties: ["Orthopedics", "Oncology"],
    accreditation: "NABH Accredited",
    image: "https://images.unsplash.com/photo-1719934398679-d764c1410770?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBob3NwaXRhbHxlbnwwfHx8fDE3NjkwNjk4MDJ8MA&ixlib=rb-4.1.0&q=85",
    description: "Modern healthcare in Mumbai region",
    beds: "350+",
    patientsPerYear: "1.3 Million+",
    featured: false,
    group: "Apollo"
  },
  
  // Additional Fortis Hospitals
  {
    id: 25,
    name: "Fortis Flt. Lt. Rajan Dhall Hospital",
    location: "Vasant Kunj, New Delhi, India",
    specialties: ["Orthopedics", "Oncology"],
    accreditation: "NABH Accredited",
    image: "https://images.pexels.com/photos/7250797/pexels-photo-7250797.jpeg",
    description: "Comprehensive healthcare in South Delhi",
    beds: "200+",
    patientsPerYear: "800,000+",
    featured: false,
    group: "Fortis"
  },
  {
    id: 26,
    name: "Fortis Hospital",
    location: "Noida, India",
    specialties: ["Orthopedics", "Oncology"],
    accreditation: "NABH Accredited",
    image: "https://images.unsplash.com/photo-1615770922480-0b9ae80afeba?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBob3NwaXRhbHxlbnwwfHx8fDE3NjkwNjk4MDJ8MA&ixlib=rb-4.1.0&q=85",
    description: "Quality healthcare in NCR",
    beds: "240+",
    patientsPerYear: "900,000+",
    featured: false,
    group: "Fortis"
  },
  {
    id: 27,
    name: "Fortis Hospital",
    location: "Mohali, Punjab, India",
    specialties: ["Orthopedics", "Oncology"],
    accreditation: "NABH Accredited",
    image: "https://images.pexels.com/photos/7250788/pexels-photo-7250788.jpeg",
    description: "Advanced care in Punjab",
    beds: "355+",
    patientsPerYear: "1 Million+",
    featured: false,
    group: "Fortis"
  },
  {
    id: 28,
    name: "Fortis Hospital",
    location: "Bangalore, India",
    specialties: ["Orthopedics", "Oncology"],
    accreditation: "NABH Accredited",
    image: "https://images.unsplash.com/photo-1719934398679-d764c1410770?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBob3NwaXRhbHxlbnwwfHx8fDE3NjkwNjk4MDJ8MA&ixlib=rb-4.1.0&q=85",
    description: "Multi-specialty care in South India",
    beds: "400+",
    patientsPerYear: "1.5 Million+",
    featured: false,
    group: "Fortis"
  },
  {
    id: 29,
    name: "Fortis Hospital",
    location: "Mumbai, India",
    specialties: ["Orthopedics", "Oncology"],
    accreditation: "NABH Accredited",
    image: "https://images.pexels.com/photos/7250797/pexels-photo-7250797.jpeg",
    description: "Comprehensive healthcare in Mumbai",
    beds: "300+",
    patientsPerYear: "1.2 Million+",
    featured: false,
    group: "Fortis"
  },
  {
    id: 30,
    name: "Fortis Hospital",
    location: "Kolkata, India",
    specialties: ["Orthopedics", "Oncology"],
    accreditation: "NABH Accredited",
    image: "https://images.unsplash.com/photo-1615770922480-0b9ae80afeba?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBob3NwaXRhbHxlbnwwfHx8fDE3NjkwNjk4MDJ8MA&ixlib=rb-4.1.0&q=85",
    description: "Quality care in Eastern India",
    beds: "150+",
    patientsPerYear: "600,000+",
    featured: false,
    group: "Fortis"
  }
];

export const doctors = [
  // Orthopedic Surgeons - Real Profiles with Real Photos
  {
    id: 1,
    name: "Dr. Ramneek Mahajan",
    specialty: "Orthopedic Surgeon",
    qualification: "MBBS, MS, MCh Ortho, Fellowship (Singapore, Australia, Germany)",
    experience: "25+ years",
    image: "/doctors/dr_ramneek_mahajan.png",
    hospital: "Max Super Speciality Hospital, Saket",
    specialization: "Joint Replacement, Hip & Knee, Robotic Surgery",
    featured: true
  },
  {
    id: 2,
    name: "Dr. Ashok Rajgopal",
    specialty: "Orthopedic Surgeon",
    qualification: "MBBS, MS Ortho (AIIMS), MCh (Liverpool), FRCS",
    experience: "45+ years",
    image: "/doctors/image1.png",
    hospital: "Medanta - The Medicity, Gurugram",
    specialization: "Knee Replacement, Arthroscopy, Robotic Joint Surgery",
    featured: true
  },
  {
    id: 3,
    name: "Dr. IPS Oberoi",
    specialty: "Orthopedic Surgeon",
    qualification: "MS Ortho, MCh Orth (Liverpool), Fellowships (Germany, Switzerland)",
    experience: "35+ years",
    image: "/doctors/image4.png",
    hospital: "Artemis Hospital, Gurugram",
    specialization: "Robotic Joint Replacement, Arthroscopy, Sports Injuries",
    featured: true
  },
  {
    id: 4,
    name: "Dr. Vijay C. Bose",
    specialty: "Orthopedic Surgeon",
    qualification: "MBBS, MS Ortho, DNB, MCh Orth (Liverpool), FRCS",
    experience: "30+ years",
    image: "/doctors/image3.png",
    hospital: "SIMS Hospital, Chennai",
    specialization: "Hip Resurfacing, Complex Joint Reconstruction",
    featured: true
  },
  {
    id: 5,
    name: "Dr. Yash Gulati",
    specialty: "Orthopedic Surgeon",
    qualification: "MBBS, MS Ortho, MCh (Liverpool), Diploma Sports Medicine",
    experience: "35+ years",
    image: "/doctors/image2.png",
    hospital: "Indraprastha Apollo Hospitals, Delhi",
    specialization: "Joint Replacement, Spine Surgery, Sports Injuries",
    featured: true
  },
  
  // Oncologists - Real Profiles from Document
  {
    id: 6,
    name: "Dr. Arvinder Singh Soin",
    specialty: "Oncologist",
    qualification: "MBBS (AIIMS), MS, FRCS (Edinburgh), FRCS (Glasgow)",
    experience: "38+ years",
    image: "/doctors/dr_arvinder_singh_soin.jpg",
    hospital: "Medanta - The Medicity, Gurugram",
    specialization: "Liver Cancer, HPB Oncology, Liver Transplant, Pioneer of Liver Transplantation in India",
    featured: true
  },
  {
    id: 7,
    name: "Dr. Subhash Gupta",
    specialty: "Oncologist",
    qualification: "MBBS (AIIMS), MS, FRCS (Edinburgh)",
    experience: "30+ years",
    image: "/doctors/dr_subhash_gupta.jpg",
    hospital: "Max Super Speciality Hospital, Saket",
    specialization: "Liver & Pancreatic Cancer Surgery, HPB Oncology, 3000+ Liver Transplants",
    featured: true
  },
  {
    id: 8,
    name: "Dr. Adarsh Chaudhary",
    specialty: "Oncologist",
    qualification: "MBBS, MS (PGIMER Chandigarh), FRCS (Edinburgh)",
    experience: "47+ years",
    image: "/doctors/dr_adarsh_chaudhary.jpg",
    hospital: "Medanta - The Medicity, Gurugram",
    specialization: "GI & Abdominal Cancer Surgery, Pancreatic Oncology, 20,000+ Surgeries",
    featured: true
  },
  {
    id: 9,
    name: "Dr. P. Raghu Ram",
    specialty: "Oncologist",
    qualification: "FRCS (UK), Honorary FRCS (Glasgow), Padma Shri Awardee",
    experience: "29+ years",
    image: "/doctors/dr_p_raghu_ram.jpg",
    hospital: "KIMS-Ushalakshmi Centre for Breast Diseases, Hyderabad",
    specialization: "Breast Surgical Oncology, Oncoplastic Surgery, South Asia's First Breast Health Centre",
    featured: true
  },
  {
    id: 10,
    name: "Dr. Mohan Keshavamurthy",
    specialty: "Oncologist",
    qualification: "MBBS, MS, MCh Urology, Fellowship Uro-Oncology (Tata Memorial), FRCS, FASTS",
    experience: "34+ years",
    image: "/doctors/dr_mohan_keshavamurthy.jpg",
    hospital: "Fortis Hospital, Bangalore",
    specialization: "Uro-Oncology, Prostate, Kidney, Bladder Cancer, Robotic Surgery",
    featured: true
  },

  // Dental Specialists - Real Profiles from Document
  {
    id: 11,
    name: "Dr. Anurag Singh",
    specialty: "Dental Specialist",
    qualification: "BDS, MDS (Oral & Maxillofacial Surgery), Fellowship in Implantology",
    experience: "25+ years",
    image: "/doctors/dr_anurag_singh.jpg",
    hospital: "Max Super Speciality Hospital, Saket",
    specialization: "Oral & Maxillofacial Surgery, Dental Implants, Jaw Reconstruction",
    featured: true
  },
  {
    id: 12,
    name: "Dr. Neetu Kamra",
    specialty: "Dental Specialist",
    qualification: "BDS, MDS (Periodontology), Advanced Certification in Cosmetic Dentistry",
    experience: "26+ years",
    image: "/doctors/dr_neetu_kamra.jpg",
    hospital: "Max Super Speciality Hospital, Saket",
    specialization: "Periodontology, Endodontics, Cosmetic Dentistry, Smile Makeover",
    featured: true
  },
  {
    id: 13,
    name: "Dr. Keshav Nathani",
    specialty: "Dental Specialist",
    qualification: "BDS, MDS (Prosthodontics), Fellowship in Aesthetic Dentistry",
    experience: "20+ years",
    image: "/doctors/dr_keshav_nathani.png",
    hospital: "Max Cosmetics, Delhi",
    specialization: "Cosmetic Dentistry, Veneers, Full Mouth Rehabilitation",
    featured: true
  },

  // Cosmetic Surgery Specialists - Real Profiles from Document
  {
    id: 14,
    name: "Dr. Rakesh Kumar Khazanchi",
    specialty: "Cosmetic Surgeon",
    qualification: "MBBS, MS (Surgery), MCh (Plastic Surgery), Fellowship (Microsurgery)",
    experience: "35+ years",
    image: "/doctors/dr_rakesh_kumar_khazanchi.png",
    hospital: "Medanta - The Medicity, Gurugram",
    specialization: "Rhinoplasty, Facelift, Body Contouring, Reconstructive Microsurgery",
    featured: true
  },
  {
    id: 15,
    name: "Dr. Rajat Gupta",
    specialty: "Cosmetic Surgeon",
    qualification: "MBBS, MS, MCh (Plastic Surgery), Fellowship in Aesthetic Surgery",
    experience: "18+ years",
    image: "/doctors/dr_rajat_gupta.jpg",
    hospital: "Fortis Memorial Research Institute, Gurugram",
    specialization: "Rhinoplasty, Facelift, Body Contouring, Aesthetic Surgery",
    featured: true
  },
  {
    id: 16,
    name: "Dr. Vipul Nanda",
    specialty: "Cosmetic Surgeon",
    qualification: "MBBS, MS, MCh (Plastic Surgery), UK Training",
    experience: "25+ years",
    image: "/doctors/dr_vipul_nanda.png",
    hospital: "Fortis Hospital, Delhi",
    specialization: "Breast Augmentation, Body Contouring, Post-Bariatric Surgery",
    featured: true
  },
  {
    id: 17,
    name: "Dr. Anil Behl",
    specialty: "Cosmetic Surgeon",
    qualification: "MBBS, MS, MCh (Plastic Surgery), FRCS (UK)",
    experience: "30+ years",
    image: "/doctors/dr_anil_behl.jpg",
    hospital: "Apollo Hospitals, Delhi",
    specialization: "Cosmetic Surgery, Reconstructive Microsurgery, Aesthetic Procedures",
    featured: true
  },
  {
    id: 18,
    name: "Dr. Sunil Choudhary",
    specialty: "Cosmetic Surgeon",
    qualification: "MBBS, MS (Gen. Surg.), Fellowship (New Zealand), EBOPRAS, OTDS (Royal College UK)",
    experience: "31+ years",
    image: "/doctors/dr_sunil_choudhary.png",
    hospital: "Max Super Speciality Hospital, Saket",
    specialization: "Aesthetic & Reconstructive Surgery, Craniofacial Surgery, Microsurgery, Body Contouring",
    featured: true
  }
];

export const specialties = [
  {
    id: 1,
    name: "Orthopedic Surgery",
    description: "Joint replacements, spine surgery, sports injuries, and trauma care with advanced surgical techniques",
    image: "https://images.unsplash.com/photo-1614274357232-c1336fe66ea3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHw0fHxrbmVlJTIweHJheSUyMG1lZGljYWwlMjBvcnRob3BlZGljJTIwYm9uZSUyMHNjYW58ZW58MHx8fHwxNzcwOTMxNzE3fDA&ixlib=rb-4.1.0&q=85",
    treatments: ["Hip Replacement", "Knee Replacement", "Spine Surgery", "Sports Injury Treatment"],
    colorClass: "accent-blue"
  },
  {
    id: 2,
    name: "Oncology",
    description: "Advanced cancer treatment with minimally invasive techniques, robotic surgery, and comprehensive tumor management",
    image: "https://images.unsplash.com/photo-1759813641406-980519f58b1c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwxfHxjYW5jZXIlMjBzdXJnZXJ5JTIwb25jb2xvZ3klMjBzdXJnZW9uJTIwaG9zcGl0YWwlMjBvcGVyYXRpb258ZW58MHx8fHwxNzcwOTMxNjkyfDA&ixlib=rb-4.1.0&q=85",
    treatments: ["Breast Cancer Surgery", "GI Cancer Surgery", "Head & Neck Surgery", "Robotic Cancer Surgery"],
    colorClass: "accent-pink"
  },
  {
    id: 3,
    name: "Dental Care",
    description: "Comprehensive dental services including cosmetic dentistry, implants, oral surgery, and smile makeovers",
    image: "https://images.unsplash.com/photo-1606811841689-23dfddce3e95?crop=entropy&cs=srgb&fm=jpg&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&ixlib=rb-4.1.0&q=85",
    treatments: ["Dental Implants", "Cosmetic Dentistry", "Oral Surgery", "Smile Makeover"],
    colorClass: "accent-green"
  },
  {
    id: 4,
    name: "Cosmetic Surgery",
    description: "Aesthetic and reconstructive procedures including rhinoplasty, facelifts, and body contouring by expert plastic surgeons",
    image: "https://images.unsplash.com/photo-1552693673-1bf958298935?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHw0fHxhZXN0aGV0aWMlMjBmYWNpYWwlMjByZWp1dmVuYXRpb24lMjBiZWF1dHklMjBjbGluaWMlMjB3b21hbnxlbnwwfHx8fDE3NzA5MzE3MTZ8MA&ixlib=rb-4.1.0&q=85",
    treatments: ["Rhinoplasty", "Facelift", "Body Contouring", "Breast Augmentation"],
    colorClass: "accent-purple"
  }
];

export const testimonials = [
  {
    id: 1,
    name: "Patient from Canada",
    country: "Canada",
    procedure: "Robotic Knee Replacement",
    rating: 5,
    comment: "I travelled from Canada for robotic knee replacement surgery to India. From the first consultation to post-surgery rehabilitation, the entire process was smooth and reassuring. The precision of the robotic procedure gave me confidence, and I was able to start walking comfortably much sooner than I expected. The level of care and professionalism exceeded my expectations.",
    colorClass: "accent-pink"
  },
  {
    id: 2,
    name: "Patient from Ireland",
    country: "Ireland",
    procedure: "Giant Cell Tumor Surgery",
    rating: 5,
    comment: "I am a 24-year-old from Ireland who had been struggling with swelling and intense pain in my right wrist for over a year, which made it difficult to perform even basic daily tasks. After being diagnosed with a giant cell tumor that had severely damaged the bone, I underwent a successful radialization of the ulna. Just eight days after the surgery, I am completely pain-free.",
    colorClass: "accent-green"
  },
  {
    id: 3,
    name: "Patient from Australia",
    country: "Australia",
    procedure: "Rhinoplasty",
    rating: 5,
    comment: "I traveled all the way from Australia to address several concerns I had with my nose, specifically a dorsal hump, wide sides, and nasal flaring. My surgeon in India was able to straighten the slope and bridge while narrowing the profile, producing exactly the result I was looking for. I am very impressed and happy with how it turned out.",
    colorClass: "accent-blue"
  },
  {
    id: 4,
    name: "Patient from Australia",
    country: "Australia",
    procedure: "Dental Treatment",
    rating: 5,
    comment: "I am currently living in Australia and originally chose to come to India for dental treatment because of the cost, but the quality of care I received was far beyond my expectations. The professionals here are truly world-class and displayed a level of patience and dedication that I haven't experienced back home. The dentists take their time with you, ensuring you feel completely comfortable and well-guided throughout the process. Beyond the clinical expertise, the kindness and helpfulness of the people in India made me feel right at home in a new city. I would highly recommend traveling here to anyone seeking exceptional dental care.",
    colorClass: "accent-purple"
  }
];

export const whyChooseUs = [
  {
    id: 1,
    title: "World-Class Hospitals",
    description: "JCI and NABH accredited hospitals with international standards",
    icon: "Building2"
  },
  {
    id: 2,
    title: "Expert Doctors",
    description: "Highly qualified surgeons with international training and experience",
    icon: "Stethoscope"
  },
  {
    id: 3,
    title: "Cost Effective",
    description: "Save up to 70% compared to Western countries without compromising quality",
    icon: "BadgeDollarSign"
  },
  {
    id: 4,
    title: "Personalized Care",
    description: "Dedicated assistance from arrival to recovery and follow-up",
    icon: "Heart"
  },
  {
    id: 5,
    title: "No Waiting Time",
    description: "Quick appointment scheduling and minimal waiting periods",
    icon: "Clock"
  },
  {
    id: 6,
    title: "Complete Support",
    description: "Visa assistance, accommodation, and travel arrangements handled",
    icon: "HeadphonesIcon"
  }
];

export const procedureOptions = [
  "Orthopedic Surgery",
  "Oncology (Cancer Treatment)",
  "Dental Care",
  "Cosmetic Surgery",
  "Other"
];

export const timeframeOptions = [
  "Within 1 month",
  "1-3 months",
  "3-6 months",
  "6+ months"
];

export const contactInfo = {
  phone: "+91-9650033182",
  whatsapp: "+61-481108023",
  email: "hello@curispath.com",
  address: "Offices in Sydney, Australia & Delhi NCR, India",
  hours: "24/7 Available"
};
